import React, { useState } from "react";
import { Button, toast } from "@jsq/ds/core";
import { useBulkUploadGeneratePresignedUrlsMutation } from "~/graphql/supergraphHooks";
import type { BulkUploadRowData } from "./bulkUploadGraphqlDataSource";

interface DetailsCellRendererProps {
  data?: BulkUploadRowData | null;
}

export const DetailsCellRenderer: React.FC<DetailsCellRendererProps> = ({ data }) => {
  const [isDownloading, setIsDownloading] = useState(false);
  const [generatePresignedUrlsMutation] = useBulkUploadGeneratePresignedUrlsMutation();

  if (!data) {
    return null;
  }

  const parsedData = data.data;

  if (!parsedData) {
    return null;
  }

  const errors = parsedData.errors ?? [];
  const hasErrors = errors.length > 0;
  const firstError = hasErrors ? errors[0] : null;

  if (hasErrors && firstError) {
    return firstError;
  }

  if (!parsedData.output_s3_key) {
    return null;
  }

  const handleDownload = async () => {
    const s3Key = parsedData.output_s3_key;

    setIsDownloading(true);
    try {
      const response = await generatePresignedUrlsMutation({
        variables: {
          urlGeneratorInput: [
            {
              name: s3Key,
              type: "text/csv",
              clientMethod: "GET_OBJECT",
              size: 0,
            },
          ],
        },
      });

      const presignedUrl = response.data?.generatePresignedUrls?.presignedUrlObjs?.[0]?.s3Url;

      if (!presignedUrl) {
        toast.error("Failed to generate download URL");
        return;
      }

      window.open(presignedUrl, "_blank", "noopener,noreferrer");
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : "Failed to download file";
      toast.error(errorMessage);
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <Button displayAsLink icon="download" onClick={handleDownload} isLoading={isDownloading}>
      Download
    </Button>
  );
};
